import React from "react";
import { useTranslation } from "react-i18next";

import { PaymentMethod } from "./types";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
    SmallToggle,
    LanguageTabs,
    LanguageTab,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    VisibilityButton,
    ResetButton,
    ResetAllButton,
    MethodSection,
    MethodSectionHeader,
    MethodIconCircle,
    MethodName,
    DisabledOverlay,
} from "./styled";

// ─── SVG Icons ──────────────────────────────────────────

const EyeIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </svg>
);

const EyeOffIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
    </svg>
);

const ResetIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
);

type PaymentMethodsCardProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedLang: string;
    onLanguageChange: (lang: string) => void;
    paymentEnabled: boolean;
    onPaymentEnabledToggle: () => void;
    paymentMethods: PaymentMethod[];
    defaultPaymentMethods: PaymentMethod[];
    methodDescVisible: Record<string, boolean>;
    onToggleMethodDescVisible: (id: string) => void;
    onTogglePaymentMethod: (id: string) => void;
    onPaymentMethodTitleChange: (id: string, value: string) => void;
    onPaymentMethodDescChange: (id: string, value: string) => void;
    onResetMethodTitle: (id: string) => void;
    onResetMethodDesc: (id: string) => void;
};

export function PaymentMethodsCard({
    mainColor,
    languages,
    selectedLang,
    onLanguageChange,
    paymentEnabled,
    onPaymentEnabledToggle,
    paymentMethods,
    defaultPaymentMethods,
    methodDescVisible,
    onToggleMethodDescVisible,
    onTogglePaymentMethod,
    onPaymentMethodTitleChange,
    onPaymentMethodDescChange,
    onResetMethodTitle,
    onResetMethodDesc,
}: PaymentMethodsCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-payment-methods-card">
            <SectionHeader>
                <SectionTitle>{t("Payment methods")}</SectionTitle>
                <ToggleContainer>
                    <ToggleLabel>
                        {paymentEnabled
                            ? t("Active")
                            : t("Disable advertising")}
                    </ToggleLabel>
                    <Toggle
                        data-testid="scan-to-payment-toggle-enabled"
                        isActive={paymentEnabled}
                        mainColor={mainColor}
                        onClick={onPaymentEnabledToggle}
                        aria-label={t("Enable payment")}
                    />
                </ToggleContainer>
            </SectionHeader>

            {paymentEnabled ? (
                <>
                    <SectionTitle style={{ marginBottom: "15px" }}>
                        {t("Title and Description")}
                    </SectionTitle>

                    <LanguageTabs data-testid="scan-to-payment-methods-language-tabs">
                        {languages.map((lang) => (
                            <LanguageTab
                                key={lang.code}
                                data-testid={`scan-to-payment-methods-lang-${lang.code}`}
                                isActive={selectedLang === lang.code}
                                mainColor={mainColor}
                                onClick={() => onLanguageChange(lang.code)}
                            >
                                {lang.label}
                            </LanguageTab>
                        ))}
                    </LanguageTabs>

                    <div
                        style={{
                            borderTop: "1px solid #e0e0e0",
                            paddingTop: "10px",
                        }}
                    >
                        {paymentMethods.map((method) => {
                            const def = defaultPaymentMethods.find(
                                (d) => d.id === method.id
                            )!;
                            return (
                                <MethodSection
                                    key={method.id}
                                    data-testid={`scan-to-payment-method-section-${method.id}`}
                                >
                                    <MethodSectionHeader>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                            }}
                                        >
                                            <MethodIconCircle
                                                mainColor={mainColor}
                                            >
                                                <svg viewBox="0 0 24 24">
                                                    <path d={method.icon} />
                                                </svg>
                                            </MethodIconCircle>
                                            <MethodName>
                                                {def.title[selectedLang] ||
                                                    def.title.fr}
                                            </MethodName>
                                        </div>
                                        <SmallToggle
                                            data-testid={`scan-to-payment-toggle-method-${method.id}`}
                                            isActive={method.enabled}
                                            mainColor={mainColor}
                                            onClick={() =>
                                                onTogglePaymentMethod(method.id)
                                            }
                                            aria-label={t(
                                                "Toggle payment method"
                                            )}
                                        />
                                    </MethodSectionHeader>

                                    {/* Column headers */}
                                    <InputRow
                                        style={{
                                            borderBottom: "1px solid #e0e0e0",
                                            paddingBottom: "10px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <InputLabel></InputLabel>
                                        <InputLabel style={{ fontWeight: 500 }}>
                                            {t("Default text")}
                                        </InputLabel>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                            }}
                                        >
                                            <InputLabel
                                                style={{ fontWeight: 500 }}
                                            >
                                                {t("Customized")}
                                            </InputLabel>
                                            <ResetAllButton
                                                data-testid={`scan-to-payment-reset-method-${method.id}`}
                                                mainColor={mainColor}
                                                onClick={() => {
                                                    onResetMethodTitle(
                                                        method.id
                                                    );
                                                    onResetMethodDesc(
                                                        method.id
                                                    );
                                                }}
                                            >
                                                <ResetIcon />
                                                {t("Reset")}
                                            </ResetAllButton>
                                        </div>
                                    </InputRow>

                                    {/* Title row */}
                                    <InputRow>
                                        <InputLabel>{t("Title")}</InputLabel>
                                        <Input
                                            data-testid={`scan-to-payment-method-default-title-${method.id}`}
                                            value={
                                                def.title[selectedLang] ||
                                                def.title.fr ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-payment-method-custom-title-${method.id}`}
                                                value={
                                                    method.title[
                                                        selectedLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onPaymentMethodTitleChange(
                                                        method.id,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    def.title[selectedLang] ||
                                                    def.title.fr ||
                                                    ""
                                                }
                                            />
                                            <ResetButton
                                                data-testid={`scan-to-payment-reset-method-title-${method.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetMethodTitle(
                                                        method.id
                                                    )
                                                }
                                                title={t("Reset")}
                                            >
                                                <ResetIcon />
                                            </ResetButton>
                                        </InputWrapper>
                                    </InputRow>

                                    {/* Description row */}
                                    <InputRow>
                                        <InputLabel>
                                            {t("Description")}
                                        </InputLabel>
                                        <Input
                                            data-testid={`scan-to-payment-method-default-desc-${method.id}`}
                                            value={
                                                def.description[selectedLang] ||
                                                def.description.fr ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-payment-method-custom-desc-${method.id}`}
                                                value={
                                                    method.description[
                                                        selectedLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onPaymentMethodDescChange(
                                                        method.id,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    def.description[
                                                        selectedLang
                                                    ] ||
                                                    def.description.fr ||
                                                    ""
                                                }
                                            />
                                            <VisibilityButton
                                                data-testid={`scan-to-payment-visibility-method-desc-${method.id}`}
                                                isVisible={
                                                    methodDescVisible[
                                                        method.id
                                                    ] !== false
                                                }
                                                onClick={() =>
                                                    onToggleMethodDescVisible(
                                                        method.id
                                                    )
                                                }
                                                title={
                                                    methodDescVisible[
                                                        method.id
                                                    ] !== false
                                                        ? t("Hide")
                                                        : t("Show")
                                                }
                                            >
                                                {methodDescVisible[
                                                    method.id
                                                ] !== false ? (
                                                    <EyeIcon />
                                                ) : (
                                                    <EyeOffIcon />
                                                )}
                                            </VisibilityButton>
                                            <ResetButton
                                                data-testid={`scan-to-payment-reset-method-desc-${method.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetMethodDesc(method.id)
                                                }
                                                title={t("Reset")}
                                            >
                                                <ResetIcon />
                                            </ResetButton>
                                        </InputWrapper>
                                    </InputRow>
                                </MethodSection>
                            );
                        })}
                    </div>
                </>
            ) : (
                <DisabledOverlay>
                    {t("All actions are disabled. The section will be hidden.")}
                </DisabledOverlay>
            )}
        </Card>
    );
}
